import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../components/config/appConfig";

/* Issues store their assignee as a snapshot — { employeeId, employeeName,
   employeeEmail, role } — taken when the person was picked, so it carries no
   photo and would still carry an old one if it did. The photo lives on the
   user record, so the tables look it up here instead.

   One request serves the whole session: the directory is cached at module
   level and every screen that asks for it shares the same promise, so opening
   the backlog and then the issues list does not fetch people twice. */

const EMPTY_DIRECTORY = { byId: {}, byEmail: {}, byName: {} };

let directory = null;
let pending = null;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("accessToken")}` });

const buildDirectory = (users) => {
  const next = { byId: {}, byEmail: {}, byName: {} };
  (Array.isArray(users) ? users : []).forEach((user) => {
    const image = user?.profileImage;
    if (!image) return;
    if (user._id) next.byId[String(user._id)] = image;
    if (user.email) next.byEmail[String(user.email).toLowerCase()] = image;
    if (user.name) next.byName[String(user.name).trim().toLowerCase()] = image;
  });
  return next;
};

export const loadUserPhotos = () => {
  if (directory) return Promise.resolve(directory);
  if (!pending) {
    pending = axios
      .get(`${API_BASE_URL}/users`, { headers: authHeaders() })
      .then(({ data }) => {
        directory = buildDirectory(data?.users);
        return directory;
      })
      .catch((error) => {
        /* the coloured initial is a perfectly good stand-in, so a failed
           lookup only costs the photos — and clearing `pending` lets the next
           screen try again rather than being stuck without them */
        console.error("Could not load people photos:", error);
        pending = null;
        return EMPTY_DIRECTORY;
      });
  }
  return pending;
};

/* `person` is either the assignee snapshot or a plain name/email string. */
const photoFrom = (dir, person) => {
  if (!person) return "";
  if (typeof person === "string") {
    const key = person.trim().toLowerCase();
    return dir.byEmail[key] || dir.byName[key] || "";
  }
  return (
    person.profileImage ||
    dir.byId[String(person.employeeId || person._id || "")] ||
    dir.byEmail[String(person.employeeEmail || person.email || "").trim().toLowerCase()] ||
    dir.byName[String(person.employeeName || person.name || "").trim().toLowerCase()] ||
    ""
  );
};

/**
 * @returns {(person: object|string) => string} the person's photo, or "" when
 * they never set one — in which case the caller draws the initial.
 */
export const useUserPhotos = () => {
  const [dir, setDir] = useState(directory || EMPTY_DIRECTORY);

  useEffect(() => {
    let cancelled = false;
    loadUserPhotos().then((next) => {
      if (!cancelled) setDir(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return useCallback((person) => photoFrom(dir, person), [dir]);
};

export default useUserPhotos;
